/**
 * Single source of truth for role-based access to "view other users' work" surfaces.
 *
 * Used by:
 *   - TaskCalendarPage — drives whether the "Viewing" dropdown is shown
 *   - UnifiedTasksPage — drives whether the Team / All tabs are shown
 *
 * Returns `true` if the current user is an Owner, Admin, or Super-Admin in the
 * current workspace (and can therefore see other users' tasks). Returns `false`
 * for Manager and Team roles, who are also restricted server-side via the
 * role-enforcement check in `server.list_tasks()`.
 *
 * The hook tracks the SAME role list the backend uses, so frontend visibility
 * stays consistent with backend authorization. If you change one, change both.
 */
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useAuth } from '../contexts/AuthContext';

const ELEVATED_WORKSPACE_ROLES = ['owner', 'admin'];

export function useCanViewAllTasks() {
  const { workspaceRole } = useWorkspace();
  const { user } = useAuth();
  if (user?.role === 'super_admin') return true;
  return ELEVATED_WORKSPACE_ROLES.includes(workspaceRole);
}
